import { Box, Button, Card, CardContent, CardHeader, TextField, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { resellerCodeError, type ResellerApplication } from "@xop/dashboard-shared";
import { Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import type { JSX } from "react";

import { ApiError } from "@/lib/api";

/**
 * "Activate as a NEW reseller" (spec §5.2, §7.3). crm-api creates the
 * `Reseller` from the application fields plus what is entered here:
 *
 *  - name: pre-filled with the company name, editable because a reseller
 *    with that exact name may already exist — crm-api refuses with
 *    "A reseller named X already exists. Link to it, or change the name."
 *    and that message is shown right here, above the editable field.
 *  - code: pre-filled with `suggested_code` (the next free 3-digit value,
 *    same allocator staff use elsewhere), editable, never 700. A reseller
 *    created through this path can therefore never be codeless.
 *
 * The button is disabled WITH the reason until both countersigned files
 * exist; crm-api enforces the same rule.
 */
export function ActivateAsNewCard({
  application,
  canManage,
  blocker,
  isSubmitting,
  error,
  onActivate,
}: {
  application: ResellerApplication;
  canManage: boolean;
  blocker: string | null;
  isSubmitting: boolean;
  error: ApiError | null;
  onActivate: (input: { name: string; code: string }) => Promise<void>;
}): JSX.Element {
  const [name, setName] = useState(application.company_name);
  const [code, setCode] = useState(application.suggested_code ?? "");
  // A fresh show (after a countersign upload) may carry a different
  // suggested code; adopt it only while the reviewer has not typed one.
  useEffect(() => {
    setCode((current) => (current === "" ? application.suggested_code ?? "" : current));
  }, [application.suggested_code]);

  const codeError = resellerCodeError(code);
  const nameEmpty = name.trim() === "";
  const disabled = !canManage || blocker !== null || codeError !== null || nameEmpty || isSubmitting;

  return (
    <Card data-cy="activate-as-new">
      <CardHeader
        title={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: "1.125rem", fontWeight: 600 }}>
            <Box sx={{ color: "text.secondary", display: "flex" }}><Sparkles size={20} /></Box>
            <span>Activate as a new reseller</span>
          </Box>
        }
        subheader={
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
            Creates a reseller from the application, with {application.email} as its owner.
          </Typography>
        }
      />
      <CardContent sx={{ display: "grid", gap: 2 }}>
        <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { sm: "1fr 160px" } }}>
          <TextField
            size="small"
            label="Reseller name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={!canManage || isSubmitting}
            inputProps={{ "data-cy": "activate-new-name" }}
            helperText="Pre-filled from the application. Change it if a reseller with this name already exists."
          />
          <TextField
            size="small"
            label="Reseller code"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 3))}
            disabled={!canManage || isSubmitting}
            error={codeError !== null}
            helperText={codeError ?? "Next free 3-digit code."}
            inputProps={{ "data-cy": "activate-new-code", inputMode: "numeric", maxLength: 3 }}
          />
        </Box>

        {error && (
          <Box role="alert" data-testid="activate-new-error" data-cy="activate-new-error" sx={{ borderRadius: 1, border: 1, borderColor: (t) => alpha(t.palette.error.main, 0.4), bgcolor: (t) => alpha(t.palette.error.main, 0.12), px: 1.5, py: 1 }}>
            <Typography sx={{ fontSize: 13, color: "error.main" }}>{error.message}</Typography>
          </Box>
        )}

        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <Button
            variant="contained"
            disabled={disabled}
            onClick={() => void onActivate({ name: name.trim(), code: code.trim() }).catch(() => undefined)}
            data-cy="activate-new-submit"
          >
            {isSubmitting ? "Activating…" : "Activate as a new reseller"}
          </Button>
          {blocker && (
            <Typography data-testid="activate-new-blocker" data-cy="activate-new-blocker" sx={{ fontSize: 12, color: "text.disabled" }}>
              {blocker}
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}
